import { User, Crown } from "lucide-react";
import { cn } from "@/lib/utils";
import { SubscriptionTier } from "@/hooks/useSubscription";

interface UserAvatarProps {
  avatarUrl?: string | null;
  name?: string;
  tier?: SubscriptionTier;
  size?: "sm" | "md" | "lg";
  showBadge?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-14 h-14",
};

const iconSizes = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-7 h-7",
};

const badgeSizes = {
  sm: "w-4 h-4 -bottom-0.5 -right-0.5",
  md: "w-5 h-5 -bottom-0.5 -right-0.5",
  lg: "w-6 h-6 -bottom-1 -right-1",
};

const crownSizes = {
  sm: "w-2.5 h-2.5",
  md: "w-3 h-3",
  lg: "w-3.5 h-3.5",
};

const UserAvatar = ({ 
  avatarUrl, 
  name, 
  tier, 
  size = "md", 
  showBadge = true,
  className 
}: UserAvatarProps) => {
  const isVip = tier === "pro" || tier === "enterprise";
  const badgeColor = tier === "enterprise" 
    ? "bg-gradient-to-br from-amber-400 to-amber-600 border-amber-300" 
    : "bg-gradient-to-br from-purple-400 to-purple-600 border-purple-300";

  return (
    <div className={cn("relative", className)}>
      <div 
        className={cn(
          "rounded-full bg-secondary flex items-center justify-center overflow-hidden",
          isVip && showBadge && (tier === "enterprise" 
            ? "ring-2 ring-amber-500/50" 
            : "ring-2 ring-purple-500/50"),
          sizeClasses[size]
        )}
      >
        {avatarUrl ? (
          <img 
            src={avatarUrl} 
            alt={name || "User"} 
            className="w-full h-full object-cover"
          />
        ) : (
          <User className={cn("text-muted-foreground", iconSizes[size])} />
        )}
      </div>
      
      {/* VIP Badge */}
      {isVip && showBadge && (
        <div 
          className={cn(
            "absolute flex items-center justify-center rounded-full border-2 border-background shadow-lg",
            badgeColor,
            badgeSizes[size]
          )}
        >
          <Crown className={cn("text-white", crownSizes[size])} />
        </div>
      )}
    </div>
  );
};

export default UserAvatar;